import React, { useEffect, useMemo, useState } from 'react'
import {
  FileText,
  CheckCircle,
  AlertCircle,
  Printer,
  X,
} from 'lucide-react'
import { Button } from '../ui/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/Dialog'
import { useHubData } from '../../hooks/useHubData'
import { useExchange } from '../../contexts/ExchangeContext'
import { wizardApi } from '../../services/api/wizard'
import { mapToForm8824, formatFieldValue, type Form8824Field } from '../../utils/form8824Mapping'

const SOURCE_COLORS: Record<string, string> = {
  sales_hub: 'bg-green-500/10 text-green-600 dark:text-green-400',
  identification_hub: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  purchase_hub: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  disbursement_hub: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  calculator: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
  manual: 'bg-muted text-muted-foreground',
}

interface Form8824PreviewProps {
  onClose: () => void
}

function renderField(field: Form8824Field) {
  return (
    <div
      key={field.line}
      className={`flex items-start gap-3 py-2.5 px-3 rounded-lg transition-colors ${
        field.filled
          ? 'bg-card'
          : 'bg-amber-50/50 dark:bg-amber-950/20'
      }`}
    >
      <span className="flex-shrink-0 w-7 h-7 rounded-md bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
        {field.line}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground">{field.label}</p>
        <div className="mt-1 flex items-center gap-2">
          {field.filled ? (
            <>
              <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
              <span className="text-sm font-semibold text-foreground truncate">
                {formatFieldValue(field.value)}
              </span>
            </>
          ) : (
            <>
              <AlertCircle className="h-3 w-3 text-amber-400 flex-shrink-0" />
              <span className="text-sm text-muted-foreground italic">
                Needs entry
              </span>
            </>
          )}
        </div>
      </div>
      <span
        className={`flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded ${SOURCE_COLORS[field.source] || SOURCE_COLORS.manual}`}
      >
        {field.sourceLabel}
      </span>
    </div>
  )
}

interface Form8824FieldsProps {
  filledCount: number
  totalCount: number
  partI: Form8824Field[]
  partIII: Form8824Field[]
}

function Form8824Fields({ filledCount, totalCount, partI, partIII }: Form8824FieldsProps) {
  const progressPercent = (filledCount / totalCount) * 100
  return (
    <div className="space-y-6">
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">Preview only</p>
            <p className="text-xs text-amber-800 dark:text-amber-200/90 mt-0.5">
              Values are auto-populated from your exchange data for convenience.
              Consult with a qualified tax professional before filing. Open1031 does not provide tax advice.
            </p>
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-2 pb-2 border-b border-border">
          Part I — Information on the Like-Kind Exchange
        </h3>
        <div className="space-y-1">
          {partI.map(renderField)}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-2 pb-2 border-b border-border">
          Part III — Realized Gain, Recognized Gain, and Basis
        </h3>
        <div className="space-y-1">
          {partIII.map(renderField)}
        </div>
      </div>
      <div className="pt-3 border-t border-border">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Data Sources</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(SOURCE_COLORS).map(([key, cls]) => (
            <span
              key={key}
              className={`text-[9px] font-semibold px-2 py-0.5 rounded ${cls}`}
            >
              {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

function useForm8824Data() {
  const salesData = useHubData('initial')
  const purchaseData = useHubData('acquisition')
  const { currentExchange } = useExchange()
  const [calcResults, setCalcResults] = useState<Record<string, unknown> | null>(() => {
    try {
      const raw = localStorage.getItem('calculator_results')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  useEffect(() => {
    if (!currentExchange?.id) return
    let cancelled = false
    wizardApi.getCalculatorResults(currentExchange.id).then((apiData) => {
      if (cancelled || !apiData) return
      setCalcResults(apiData as Record<string, unknown>)
      try {
        localStorage.setItem('calculator_results', JSON.stringify(apiData))
      } catch {}
    })
    return () => { cancelled = true }
  }, [currentExchange?.id])

  const formData = useMemo(() => {
    return mapToForm8824(
      salesData.data,
      null,
      purchaseData.data,
      calcResults
    )
  }, [salesData.data, purchaseData.data, calcResults])

  return formData
}

interface Form8824InlinePanelProps {
  onClose: () => void
}

export const Form8824InlinePanel: React.FC<Form8824InlinePanelProps> = ({ onClose }) => {
  const formData = useForm8824Data()
  const partI = formData.fields.filter(f => parseInt(f.line) <= 7)
  const partIII = formData.fields.filter(f => parseInt(f.line) >= 12)

  return (
    <div className="rounded-xl border-2 border-cyan-200 dark:border-cyan-800 bg-white dark:bg-slate-800 overflow-hidden">
      <div className="px-5 py-4 border-b border-cyan-100 dark:border-cyan-900 bg-cyan-50/60 dark:bg-cyan-900/10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-cyan-100 dark:bg-cyan-800/40 flex items-center justify-center flex-shrink-0">
            <FileText className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">IRS Form 8824 Preview</p>
            <p className="text-xs text-muted-foreground">
              {formData.filledCount} of {formData.totalCount} fields auto-populated from your exchange data
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
          >
            <Printer className="h-3.5 w-3.5 mr-1.5" />
            Print
          </Button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close form preview"
            className="p-1.5 rounded-md hover:bg-cyan-100 dark:hover:bg-cyan-800/40 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="px-5 py-5">
        <Form8824Fields
          filledCount={formData.filledCount}
          totalCount={formData.totalCount}
          partI={partI}
          partIII={partIII}
        />
      </div>
    </div>
  )
}

export const Form8824Preview: React.FC<Form8824PreviewProps> = ({ onClose }) => {
  const formData = useForm8824Data()
  const partI = formData.fields.filter(f => parseInt(f.line) <= 7)
  const partIII = formData.fields.filter(f => parseInt(f.line) >= 12)

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open) onClose() }}
    >
      <DialogContent className="max-w-3xl max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col p-0">
        <DialogHeader className="border-b border-border px-6 py-4 bg-primary/5 space-y-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-foreground leading-tight">
                  IRS Form 8824 Preview
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  {formData.filledCount} of {formData.totalCount} fields auto-populated from your exchange data
                </DialogDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              className="mr-8"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" />
              Print
            </Button>
          </div>
          <div className="mt-3">
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${(formData.filledCount / formData.totalCount) * 100}%` }}
              />
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <Form8824Fields
            filledCount={formData.filledCount}
            totalCount={formData.totalCount}
            partI={partI}
            partIII={partIII}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
